import { createContext, useCallback, useContext, useState } from "react";
import { Menu } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { SystemConfigResponse, SystemSettingsResponse, UpdateSystemConfigRequest, UpdateSystemSettingsRequest } from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import { Sheet, SheetContent } from "../../components/ui/sheet/index.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import {
  WORKSPACE_INTERNAL_SIDEBAR_ASIDE_CLASS,
  WORKSPACE_INTERNAL_SIDEBAR_COLLAPSED_PX,
  WORKSPACE_INTERNAL_SIDEBAR_EXPANDED_PX,
} from "../../components/layout/workspace-internal-sidebar.js";
import { WORKSPACE_SIDEBAR_COLLAPSED_KEYS } from "../../components/layout/workspace-sidebar-collapsed-preference.js";
import { useWorkspaceSidebarCollapsedPreference } from "../../components/layout/use-workspace-sidebar-collapsed-preference.js";
import { CONTACT_SHEET_OVERLAY_MOTION_CLASS } from "../contacts/contacts-table-presentation.js";
import { useOptionalNotifications } from "../notifications/notification-context.js";
import type { SuperAdminLoadState } from "./SuperAdminDashboardPage.js";
import {
  globalSettingsDraftFromSource,
  globalSettingsFormKey,
  readGlobalSettingsDraft,
  type GlobalSettingsDraft,
  type GlobalSettingsField,
  type GlobalSettingsSource,
} from "./global-settings-draft.js";
import { SuperAdminSettingsSidebar } from "./SuperAdminSettingsSidebar.js";
import {
  SUPER_ADMIN_SETTINGS_DEFAULT_PATH,
  superAdminSettingsSection,
  type SuperAdminSettingsSectionId,
} from "./super-admin-settings-nav.js";
import { SuperAdminLimitField, SuperAdminPlatformToggle, superAdminInputClassName } from "./super-admin-setting-rows.js";

export interface SuperAdminSettingsData {
  config: SystemConfigResponse;
  settings: SystemSettingsResponse;
}

type SettingsEditor = {
  draft: GlobalSettingsDraft;
  invalidFields: GlobalSettingsField[];
  pending: boolean;
  unchanged: boolean;
  saveFailed: boolean;
  updateDraft: (next: GlobalSettingsDraft) => void;
  openMobileNav: () => void;
};

const SettingsEditorContext = createContext<SettingsEditor | null>(null);

function useSettingsEditor(): SettingsEditor {
  const value = useContext(SettingsEditorContext);
  if (!value) {
    throw new Error("Super Admin settings sections must render inside the settings editor");
  }
  return value;
}

export type RenderSuperAdminTeam = (openMobileNav: () => void) => React.ReactNode;

export function SuperAdminSettingsView({
  state,
  onRetry,
  onSave,
  renderTeam,
}: {
  state: SuperAdminLoadState<SuperAdminSettingsData>;
  onRetry: () => void;
  onSave: (input: { config: UpdateSystemConfigRequest; settings: UpdateSystemSettingsRequest }) => Promise<void>;
  renderTeam: RenderSuperAdminTeam;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (state.status === "loading") {
    return (
      <div className="h-48 animate-pulse rounded-xl border border-line bg-surface" data-testid="super-admin-settings-loading" aria-busy="true" />
    );
  }

  if (state.status === "error") {
    return (
      <ProductErrorState
        testId="super-admin-settings-error"
        title={t("superAdmin.settings.loadErrorTitle")}
        description={t("superAdmin.settings.loadErrorBody")}
        onRetry={onRetry}
        retryLabel={t("superAdmin.retry")}
      />
    );
  }

  return (
    <GlobalSettingsEditor
      key={globalSettingsFormKey(state.data)}
      source={state.data}
      onSave={onSave}
      renderTeam={renderTeam}
    />
  );
}

function GlobalSettingsEditor({
  source,
  onSave,
  renderTeam,
}: {
  source: GlobalSettingsSource;
  onSave: (input: { config: UpdateSystemConfigRequest; settings: UpdateSystemSettingsRequest }) => Promise<void>;
  renderTeam: RenderSuperAdminTeam;
}): React.JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const notifications = useOptionalNotifications();
  const [draft, setDraft] = useState<GlobalSettingsDraft>(() => globalSettingsDraftFromSource(source));
  const [pending, setPending] = useState(false);
  const [invalidFields, setInvalidFields] = useState<GlobalSettingsField[]>([]);
  const [saveFailed, setSaveFailed] = useState(false);
  const [collapsed, setCollapsed] = useWorkspaceSidebarCollapsedPreference(
    WORKSPACE_SIDEBAR_COLLAPSED_KEYS.superAdminSettings,
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const section = superAdminSettingsSection(location.pathname);
  const draftResult = readGlobalSettingsDraft(source, draft);
  const unchanged =
    draftResult.ok && Object.keys(draftResult.config).length === 0 && Object.keys(draftResult.settings).length === 0;

  function updateDraft(next: GlobalSettingsDraft): void {
    setDraft(next);
    setInvalidFields([]);
    setSaveFailed(false);
  }

  async function save(): Promise<void> {
    if (!draftResult.ok) {
      setInvalidFields(draftResult.fields);
      setSaveFailed(false);
      return;
    }
    if (unchanged) {
      return;
    }
    setPending(true);
    setInvalidFields([]);
    setSaveFailed(false);
    try {
      await onSave({ config: draftResult.config, settings: draftResult.settings });
      notifications?.pushNotification({ message: t("superAdmin.settings.saved") });
    } catch {
      setSaveFailed(true);
    } finally {
      setPending(false);
    }
  }

  if (!section) {
    return <Navigate to={SUPER_ADMIN_SETTINGS_DEFAULT_PATH} replace />;
  }

  const asideWidth = collapsed
    ? WORKSPACE_INTERNAL_SIDEBAR_COLLAPSED_PX
    : WORKSPACE_INTERNAL_SIDEBAR_EXPANDED_PX;

  return (
    <SettingsEditorContext.Provider
      value={{ draft, invalidFields, pending, unchanged, saveFailed, updateDraft, openMobileNav }}
    >
      <div className="flex h-full min-h-0 bg-canvas" data-testid="super-admin-settings">
        <aside
          className={WORKSPACE_INTERNAL_SIDEBAR_ASIDE_CLASS}
          style={{ width: asideWidth }}
          data-testid="super-admin-settings-desktop-nav"
        >
          <SuperAdminSettingsSidebar collapsed={collapsed} onCollapsedChange={setCollapsed} />
        </aside>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent
              side="start"
              className="flex h-full max-h-dvh w-[min(100%,18rem)] flex-col gap-0 overflow-hidden border-e border-border/60 bg-surface p-0 text-foreground shadow-lg"
              overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
              data-testid="super-admin-settings-mobile-nav"
            >
              <SuperAdminSettingsSidebar
                variant="drawer"
                collapsed={false}
                onNavigate={closeMobileNav}
                onClose={closeMobileNav}
              />
            </SheetContent>
          </Sheet>
          {section === "team" ? (
            renderTeam(openMobileNav)
          ) : (
            <form
              className="flex min-h-0 flex-1 flex-col"
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
            >
              <SettingsSection section={section} />
            </form>
          )}
        </div>
      </div>
    </SettingsEditorContext.Provider>
  );
}

function SettingsSection({
  section,
}: {
  section: Exclude<SuperAdminSettingsSectionId, "team">;
}): React.JSX.Element {
  switch (section) {
    case "defaults":
      return <DefaultsSection />;
    case "support":
      return <SupportSection />;
    default: {
      const unreachable: never = section;
      return unreachable;
    }
  }
}

function SectionFrame({
  title,
  description,
  testId,
  children,
}: {
  title: string;
  description: string;
  testId: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const editor = useSettingsEditor();
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid={testId}>
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line/70 bg-surface px-4 py-3.5">
        <div className="min-w-0">
          <h1 className="m-0 text-[1rem] font-semibold tracking-[-0.01em] text-foreground">{title}</h1>
          <p className="m-0 mt-1 max-w-2xl text-[0.75rem] leading-5 text-muted">{description}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={editor.pending || editor.unchanged} data-testid="super-admin-settings-save">
            {editor.pending ? t("superAdmin.settings.saving") : t("superAdmin.settings.save")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 lg:hidden"
            onClick={editor.openMobileNav}
            data-testid="super-admin-settings-open-nav"
          >
            <Menu size={14} aria-hidden="true" />
            {t("superAdmin.settings.nav.mobileMenu")}
          </Button>
        </div>
      </header>
      {editor.invalidFields.length > 0 ? (
        <p className="m-0 border-b border-line/70 px-4 py-2 text-sm text-destructive" role="alert">
          {t("superAdmin.settings.invalid")}
        </p>
      ) : null}
      {editor.saveFailed ? (
        <p className="m-0 border-b border-line/70 px-4 py-2 text-sm text-destructive" role="alert">
          {t("superAdmin.settings.saveError")}
        </p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        <div className="grid w-full gap-4">{children}</div>
      </div>
    </div>
  );
}

function DefaultsSection(): React.JSX.Element {
  const { t } = useTranslation();
  const { draft, invalidFields, updateDraft } = useSettingsEditor();
  return (
    <SectionFrame
      testId="super-admin-settings-defaults"
      title={t("superAdmin.settings.defaultsTitle")}
      description={t("superAdmin.settings.defaultsOnlyNew")}
    >
      <div className="overflow-hidden rounded-xl border border-line bg-surface">
        <SuperAdminLimitField
          id="super-admin-default-contacts"
          label={t("settings.usage.contacts")}
          description={t("superAdmin.settings.contactsDescription")}
          unlimitedLabel={t("settings.usage.unlimited")}
          unlimited={draft.contactsUnlimited}
          value={draft.contactsValue}
          invalid={invalidFields.includes("contacts")}
          onUnlimitedChange={(unlimited) => updateDraft({ ...draft, contactsUnlimited: unlimited })}
          onValueChange={(value) => updateDraft({ ...draft, contactsValue: value })}
        />
        <div className="h-px bg-line" />
        <SuperAdminLimitField
          id="super-admin-default-team"
          label={t("settings.usage.team")}
          description={t("superAdmin.settings.teamDescription")}
          unlimitedLabel={t("settings.usage.unlimited")}
          unlimited={draft.teamUnlimited}
          value={draft.teamValue}
          invalid={invalidFields.includes("team")}
          onUnlimitedChange={(unlimited) => updateDraft({ ...draft, teamUnlimited: unlimited })}
          onValueChange={(value) => updateDraft({ ...draft, teamValue: value })}
        />
        <div className="h-px bg-line" />
        <SuperAdminLimitField
          id="super-admin-default-storage"
          label={t("settings.usage.storage")}
          description={t("superAdmin.settings.storageDescription")}
          value={draft.storageValue}
          unit={t("superAdmin.detail.gigabyteUnit")}
          invalid={invalidFields.includes("storage")}
          onValueChange={(value) => updateDraft({ ...draft, storageValue: value, storageTouched: true })}
        />
      </div>
      <div className="grid gap-3">
        <div>
          <h2 className="m-0 text-sm font-semibold text-foreground">{t("superAdmin.detail.entitlements")}</h2>
          <p className="m-0 mt-1 text-sm text-muted">{t("superAdmin.settings.platformsHint")}</p>
        </div>
        <ul className="m-0 list-none divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface p-0">
          <SuperAdminPlatformToggle
            platform="whatsapp"
            enabled={draft.whatsappEnabled}
            onChange={(enabled) => updateDraft({ ...draft, whatsappEnabled: enabled })}
          />
          <SuperAdminPlatformToggle
            platform="facebook"
            enabled={draft.facebookEnabled}
            onChange={(enabled) => updateDraft({ ...draft, facebookEnabled: enabled })}
          />
          <SuperAdminPlatformToggle
            platform="instagram"
            enabled={draft.instagramEnabled}
            onChange={(enabled) => updateDraft({ ...draft, instagramEnabled: enabled })}
          />
        </ul>
      </div>
    </SectionFrame>
  );
}

function SupportSection(): React.JSX.Element {
  const { t } = useTranslation();
  const { draft, invalidFields, updateDraft } = useSettingsEditor();
  return (
    <SectionFrame
      testId="super-admin-settings-support"
      title={t("superAdmin.settings.supportTitle")}
      description={t("superAdmin.settings.supportHint")}
    >
      <div className="grid gap-3 rounded-xl border border-line bg-surface px-4 py-4">
        <SupportField
          id="super-admin-support-email"
          label={t("organization.suspended.supportEmail")}
          value={draft.supportEmail}
          invalid={invalidFields.includes("supportEmail")}
          autoComplete="email"
          onChange={(value) => updateDraft({ ...draft, supportEmail: value })}
        />
        <SupportField
          id="super-admin-support-phone"
          label={t("organization.suspended.supportPhone")}
          value={draft.supportPhone}
          invalid={invalidFields.includes("supportPhone")}
          autoComplete="tel"
          onChange={(value) => updateDraft({ ...draft, supportPhone: value })}
        />
        <SupportField
          id="super-admin-support-whatsapp"
          label={t("organization.suspended.supportWhatsApp")}
          value={draft.supportWhatsApp}
          invalid={invalidFields.includes("supportWhatsApp")}
          autoComplete="tel"
          onChange={(value) => updateDraft({ ...draft, supportWhatsApp: value })}
        />
        <SupportField
          id="super-admin-support-url"
          label={t("organization.suspended.supportUrl")}
          value={draft.supportUrl}
          invalid={invalidFields.includes("supportUrl")}
          autoComplete="url"
          onChange={(value) => updateDraft({ ...draft, supportUrl: value })}
        />
      </div>
    </SectionFrame>
  );
}

function SupportField({
  id,
  label,
  value,
  invalid,
  autoComplete,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  invalid: boolean;
  autoComplete: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="grid gap-1">
      <label className="text-sm font-medium text-foreground" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        autoComplete={autoComplete}
        aria-invalid={invalid}
        placeholder={t("superAdmin.settings.supportEmpty")}
        className={superAdminInputClassName}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
